import type { FmpCongressTradeRow } from '@/services/api';

export type CongressTradeCategory = 'purchase' | 'sale_full' | 'sale_partial' | 'sale' | 'other';
export type CongressChamber = 'senate' | 'house';

export interface CongressTradeCategoryDef {
  category: CongressTradeCategory;
  label: string;
  hint: string;
  color: string;
}

export interface CongressChamberDef {
  chamber: CongressChamber;
  label: string;
  hint: string;
}

export const CONGRESS_TRADE_CATEGORY_DEFS: CongressTradeCategoryDef[] = [
  { category: 'purchase', label: 'Purchase', hint: 'Open market or private purchase', color: '#10b981' },
  { category: 'sale_full', label: 'Sale (full)', hint: 'Full position disposition', color: '#ef4444' },
  { category: 'sale_partial', label: 'Sale (partial)', hint: 'Partial position disposition', color: '#f97316' },
  { category: 'sale', label: 'Sale', hint: 'Other sale disclosures', color: '#ef4444' },
  { category: 'other', label: 'Other', hint: 'Exchange, swap, or uncategorized', color: '#6b7280' },
];

export const CONGRESS_CHAMBER_DEFS: CongressChamberDef[] = [
  { chamber: 'senate', label: 'Senate', hint: 'Senate periodic transaction report' },
  { chamber: 'house', label: 'House', hint: 'House periodic transaction report' },
];

export const CONGRESS_TRADING_ENABLED_CATEGORIES_KEY = 'congressTradingEnabledCategories';
export const CONGRESS_TRADING_ENABLED_CHAMBERS_KEY = 'congressTradingEnabledChambers';

const DEFAULT_ENABLED_CATEGORIES = CONGRESS_TRADE_CATEGORY_DEFS.map((d) => d.category);
const DEFAULT_ENABLED_CHAMBERS = CONGRESS_CHAMBER_DEFS.map((d) => d.chamber);

export function classifyCongressTrade(trade: FmpCongressTradeRow): CongressTradeCategory {
  const t = (trade.type ?? '').trim().toLowerCase();
  if (t === 'purchase') return 'purchase';
  if (t === 'sale (full)') return 'sale_full';
  if (t === 'sale (partial)') return 'sale_partial';
  if (t.startsWith('sale')) return 'sale';
  return 'other';
}

export function congressTradeCategoryLabel(trade: FmpCongressTradeRow): string {
  const tt = (trade.type ?? '').trim();
  if (tt) return tt;
  const def = CONGRESS_TRADE_CATEGORY_DEFS.find((d) => d.category === classifyCongressTrade(trade));
  return def?.label ?? 'Other';
}

export function congressTradeDiamondColor(trade: FmpCongressTradeRow): string {
  const category = classifyCongressTrade(trade);
  const def = CONGRESS_TRADE_CATEGORY_DEFS.find((d) => d.category === category);
  return def?.color ?? '#6b7280';
}

export function congressTradeChamber(trade: FmpCongressTradeRow): CongressChamber | null {
  const c = (trade.chamber ?? '').trim().toLowerCase();
  if (c === 'senate' || c === 'house') return c;
  return null;
}

export function congressMemberName(trade: FmpCongressTradeRow): string {
  const office = (trade.office ?? '').trim();
  if (office) return office;
  return [trade.firstName, trade.lastName].filter(Boolean).join(' ').trim() || 'Unknown';
}

/** Stable key for search highlight (stack index excluded). */
export function congressTradeIdentityKey(trade: FmpCongressTradeRow): string {
  return [
    trade.chamber ?? '',
    trade.firstName ?? '',
    trade.lastName ?? '',
    trade.transactionDate ?? '',
    trade.type ?? '',
    trade.amount ?? '',
    trade.district ?? '',
  ].join('|');
}

export function fmpCongressTradeMarkerKey(
  date: string,
  trade: FmpCongressTradeRow,
  stackIndex: number
): string {
  return [date, String(stackIndex), congressTradeIdentityKey(trade)].join('|');
}

export function readCongressTradingEnabledCategories(): Set<CongressTradeCategory> {
  if (typeof window === 'undefined') return new Set(DEFAULT_ENABLED_CATEGORIES);
  try {
    const raw = window.localStorage.getItem(CONGRESS_TRADING_ENABLED_CATEGORIES_KEY);
    if (!raw) return new Set(DEFAULT_ENABLED_CATEGORIES);
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set(DEFAULT_ENABLED_CATEGORIES);
    const valid = new Set(CONGRESS_TRADE_CATEGORY_DEFS.map((d) => d.category));
    const categories = parsed.filter(
      (c): c is CongressTradeCategory => typeof c === 'string' && valid.has(c as CongressTradeCategory)
    );
    return categories.length > 0 ? new Set(categories) : new Set(DEFAULT_ENABLED_CATEGORIES);
  } catch {
    return new Set(DEFAULT_ENABLED_CATEGORIES);
  }
}

export function persistCongressTradingEnabledCategories(categories: ReadonlySet<CongressTradeCategory>): void {
  try {
    window.localStorage.setItem(
      CONGRESS_TRADING_ENABLED_CATEGORIES_KEY,
      JSON.stringify([...categories])
    );
  } catch {
    /* ignore */
  }
}

export function readCongressTradingEnabledChambers(): Set<CongressChamber> {
  if (typeof window === 'undefined') return new Set(DEFAULT_ENABLED_CHAMBERS);
  try {
    const raw = window.localStorage.getItem(CONGRESS_TRADING_ENABLED_CHAMBERS_KEY);
    if (!raw) return new Set(DEFAULT_ENABLED_CHAMBERS);
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set(DEFAULT_ENABLED_CHAMBERS);
    const valid = new Set(CONGRESS_CHAMBER_DEFS.map((d) => d.chamber));
    const chambers = parsed.filter(
      (c): c is CongressChamber => typeof c === 'string' && valid.has(c as CongressChamber)
    );
    return chambers.length > 0 ? new Set(chambers) : new Set(DEFAULT_ENABLED_CHAMBERS);
  } catch {
    return new Set(DEFAULT_ENABLED_CHAMBERS);
  }
}

export function persistCongressTradingEnabledChambers(chambers: ReadonlySet<CongressChamber>): void {
  try {
    window.localStorage.setItem(
      CONGRESS_TRADING_ENABLED_CHAMBERS_KEY,
      JSON.stringify([...chambers])
    );
  } catch {
    /* ignore */
  }
}

export function tradeMatchesEnabledCongressCategories(
  trade: FmpCongressTradeRow,
  enabledCategories: ReadonlySet<CongressTradeCategory>
): boolean {
  if (enabledCategories.size === 0) return false;
  return enabledCategories.has(classifyCongressTrade(trade));
}

export function tradeMatchesEnabledCongressChambers(
  trade: FmpCongressTradeRow,
  enabledChambers: ReadonlySet<CongressChamber>
): boolean {
  if (enabledChambers.size === 0) return false;
  const chamber = congressTradeChamber(trade);
  if (!chamber) return true;
  return enabledChambers.has(chamber);
}

export function countCongressTradesByCategory(
  trades: FmpCongressTradeRow[] | undefined
): Map<CongressTradeCategory, number> {
  const counts = new Map<CongressTradeCategory, number>();
  if (!trades?.length) return counts;
  for (const t of trades) {
    const category = classifyCongressTrade(t);
    counts.set(category, (counts.get(category) ?? 0) + 1);
  }
  return counts;
}

export function countCongressTradesByChamber(
  trades: FmpCongressTradeRow[] | undefined
): Map<CongressChamber, number> {
  const counts = new Map<CongressChamber, number>();
  if (!trades?.length) return counts;
  for (const t of trades) {
    const chamber = congressTradeChamber(t);
    if (!chamber) continue;
    counts.set(chamber, (counts.get(chamber) ?? 0) + 1);
  }
  return counts;
}
